import { useEffect, useMemo } from 'react';

import { PermissionType } from 'api/types/entity';
import { Action, ContentHeader } from 'components';
import { getYear, setYear } from 'date-fns';
import { lodash } from 'helpers';
import { useTranslation } from 'libs/i18n';
import { usePermissions } from 'libs/permissions';
import { YearPicker } from 'libs/ui/Calendar/YearPicker';
import { Icon } from 'libs/ui/Icon';
import { useDirectoryContext } from 'modules/directory/common/providers/DirectoryProvider/useDirectoryContext';

import { useFormContext } from '../../forms/productionCalendarForm';
import { useProductionCalendarContext } from '../../providers';

enum ActionType {
  edit = 'edit',
}

export const Header = () => {
  const { readOnly, setReadOnly, resetForm } = useFormContext();
  const { t } = useTranslation();

  const { onEnableEdit } = useDirectoryContext();
  const { currentDate, setCurrentDate } = useProductionCalendarContext();

  const { checkPermissions } = usePermissions();

  const editable = !readOnly;

  const showYearPicker = !editable;

  const currentYear = getYear(currentDate);

  const actions = useMemo(
    () =>
      lodash.compact([
        checkPermissions([PermissionType.Directory_UpdateOne]) && {
          value: ActionType.edit,
          label: t('common.edit'),
          Icon: Icon.Edit,
        },
      ]),
    [checkPermissions, t]
  );

  const onClickOption = (action: ActionType) => {
    switch (action) {
      case ActionType.edit:
        onEnableEdit(() => setReadOnly(false));
        break;
      default:
        break;
    }
  };

  const onYearChange = (year: number) => {
    setCurrentDate((prev) => setYear(prev, year));
  };

  useEffect(() => {
    if (!editable) {
      resetForm();
    }
  }, [editable, resetForm]);

  return (
    <ContentHeader
      header={t('directory.productionCalendar.title')}
      headerAfterContent={
        showYearPicker ? (
          <YearPicker year={currentYear} onYearChange={onYearChange} />
        ) : null
      }
      rightContent={
        !editable && actions.length > 0 ? (
          <Action
            tooltip={t('common.edit')}
            onClick={() => onClickOption(ActionType.edit)}
          >
            <Icon.Edit size={16} />
          </Action>
        ) : null
      }
    />
  );
};
